import { useSuspenseQuery } from '@apollo/client';
import { MetaData, UserOnlineState } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { memo, useDeferredValue, useEffect, useState } from 'react';
import MediaQuery from 'react-responsive';

import { gengql } from '@/__generated__';
import { useAuth } from '@/auth';
import Button from '@/components/Button';
import ConsultationRequestRouter from '@/components/ConsultationRequestRouter';
import MaterialIcon from '@/components/Icon/MaterialIcon';
import Link from '@/components/Link';
import Picture from '@/components/Picture/Picture';
import Tidbit from '@/components/Tidbit/Tidbit';
import { canRequestConsultation } from '@/consultation';
import { ERROR_CODES, hasOnlyErrorCode } from '@/core/api';
import { isGroupContext } from '@/core/user';
import { useAppSelector } from '@/store';
import { SCREEN_MD } from '@/theme/screens';

import { isAnonymousUser } from '../sendbird';
import { findOtherMember, reportUnresolvableChatUser } from '../utils';
import s from './ChatHeader.module.scss';
import OnlineStatus from './OnlineStatus';

interface ChatHeaderProps {
  channel: GroupChannel;
  metadata: MetaData;
  initialAnonymous?: boolean;
  isMobileVersion?: boolean;
  onBackToChannels?: () => void;
}

const GET_USER = gengql(/* GraphQL */ `
  query getChatHeaderUser($id: String!, $groupId: String) {
    user(id: $id) {
      id
      profile {
        id
        name
        first_name
        last_name
        title
        url_endpoint
        picture_url
        html_url
        city
        country
        expert_state
        can_request_consultation
        credit_rate(group_id: $groupId)
        client_rate(group_id: $groupId)
        timezone
        languages {
          code
          fluency
        }
      }
    }
  }
`);

export const UserNotAvailable = ({
  isMobileVersion,
  onBackToChannels,
}: {
  isMobileVersion: boolean;
  onBackToChannels?: () => void;
}): JSX.Element => {
  return (
    <div className={s.header}>
      {/* On mobile the channel list is not rendered alongside the thread, so this arrow
          is the only in-pane way back. Omitting it here stranded anyone who opened a
          conversation whose counterpart was gone — a routine state since ENG-5628. */}
      {isMobileVersion && onBackToChannels && (
        <div
          className={s.backToChannels}
          role="button"
          tabIndex={0}
          aria-label="Back to channels"
          onClick={onBackToChannels}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onBackToChannels();
            }
          }}
        >
          <MaterialIcon icon="arrow_back" />
        </div>
      )}
      <div className={s.pictureContainer}>
        <Picture size={isMobileVersion ? 40 : 50} aria-label="User not available" />
      </div>
      <div className={s.messagesTitleContainer}>
        <div className={s.messagesTitle} role="status">
          <div>User not available</div>
        </div>
      </div>
    </div>
  );
};

const ChatHeader = memo(
  ({ channel, metadata, isMobileVersion = false, onBackToChannels }: ChatHeaderProps) => {
    const { viewer } = useAuth();

    const [requestConsultationOpen, setRequestConsultationOpen] = useState(false);
    // ENG-5505: price the expert for the active view-as team, as fetchProfile does for the
    // profile header (ENG-5351). When that team is billable, ConsultationRequestRouter
    // books on it, so the form quotes the booked account's rate. Outside a team context
    // the api prices on the viewer's own account while the router may book on their
    // default team. The profile page's request dialog prices unscoped unless its URL
    // carries ?groupId, so in a team context it can quote differently from this one.
    const userContext = useAppSelector((state) => state.ui.userContext);
    const groupId =
      isGroupContext(userContext) && viewer.groups?.some((group) => group.id === userContext)
        ? userContext
        : undefined;
    // A view-as switch re-prices the expert. Deferring it keeps the header, and the message
    // list in the same Suspense boundary, on screen while the new price loads rather than
    // dropping the chat to the fallback. Request Call waits until the price is current.
    const pricedGroupId = useDeferredValue(groupId);
    const isRepricing = pricedGroupId !== groupId;

    if (!viewer.id) {
      throw new Error('Chat header requires logged in user');
    }
    const otherMember = findOtherMember(channel.members, viewer.id);
    const isAnonymous = otherMember ? isAnonymousUser(metadata, otherMember.userId) : false;

    const skipLookup = isAnonymous || !otherMember;
    const { data, error: lookupError } = useSuspenseQuery(GET_USER, {
      variables: {
        id: otherMember?.userId ?? '',
        groupId: pricedGroupId,
      },
      // KT: note that this will still return cached value if skip is true
      skip: skipLookup,
      // See ChatMessage.tsx for the full rationale: a counterpart the api can no longer
      // resolve is an expected condition, so it comes back as an error, not a throw.
      errorPolicy: 'all',
    });
    const error = skipLookup ? undefined : lookupError;

    const isUnresolvable = !!error && hasOnlyErrorCode(error, ERROR_CODES.AUTH_USER_NOT_FOUND);

    // Keyed on the absent user rather than on the error, so this still holds if the api
    // ever returns a null user without an error. That also retires the
    // `throw new Error('User has no profile')` that used to sit below (ENG-5628).
    const hasNoProfile = !isAnonymous && !data?.user?.profile;

    // Both degraded paths report, not just the error one. The no-profile branch replaced a
    // `throw` that Sentry did see, so leaving it untelemetered would swap a loud failure
    // for a silent one — under its own reason tag, since its cause is not the same.
    //
    // From an effect rather than the render body, because captureMessage is a side effect
    // and a render around useSuspenseQuery can be discarded and retried.
    const otherUserId = otherMember?.userId;
    useEffect(() => {
      if (!otherUserId) return;
      if (isUnresolvable) reportUnresolvableChatUser('chat_header', otherUserId, 'user_not_found');
      else if (hasNoProfile) reportUnresolvableChatUser('chat_header', otherUserId, 'no_profile');
    }, [isUnresolvable, hasNoProfile, otherUserId]);

    if (error && !isUnresolvable) throw error;

    if (!otherMember || error || hasNoProfile) {
      return (
        <UserNotAvailable isMobileVersion={isMobileVersion} onBackToChannels={onBackToChannels} />
      );
    }

    const handleRequestConsultation = () => setRequestConsultationOpen(true);
    const closeRequestConsultation = () => setRequestConsultationOpen(false);

    const user = isAnonymous ? null : data?.user;
    const userId = user?.id;
    const profile = user?.profile;
    const showTidbit = profile?.city || profile?.country || profile?.languages?.length;

    return (
      <div className={s.header}>
        {isMobileVersion && (
          <div className={s.backToChannels}>
            <MaterialIcon icon="arrow_back" onClick={onBackToChannels} />
          </div>
        )}
        <div className={s.pictureContainer}>
          <Picture user={profile} size={isMobileVersion ? 40 : 50} />

          {otherMember.connectionStatus === UserOnlineState.ONLINE && (
            <OnlineStatus style={isMobileVersion ? { bottom: 0, right: -2 } : undefined} />
          )}
        </div>
        <div className={s.messagesTitleContainer}>
          <div className={s.messagesTitle}>
            {profile?.name ? (
              <Link to={profile.html_url || ''}>{profile.name}</Link>
            ) : (
              'Confidential Client'
            )}
          </div>
          {showTidbit && (
            <div className={s.messagesSubTitle}>
              <MediaQuery maxWidth={SCREEN_MD}>
                {(isMediumScreen: any) => (
                  <Tidbit
                    inline={!isMediumScreen}
                    city={profile.city || undefined}
                    country={profile.country || undefined}
                    languages={profile.languages || undefined}
                  />
                )}
              </MediaQuery>
            </div>
          )}
        </div>
        {/* ENG-5505: also respect the expert's availability, as the profile does. */}
        {canRequestConsultation(profile, {
          can_request_consultation: !!profile?.can_request_consultation,
        }) && (
          <div className={s.requestConsultation}>
            <Button
              size={isMobileVersion ? 'small' : 'normal'}
              onClick={handleRequestConsultation}
              disabled={isRepricing}
              style={{ fontSize: 14 }}
            >
              Request Call
            </Button>
          </div>
        )}
        {/* ENG-5505: through the router, like the profile and expert request entry
            points, so an SMB viewer gets the dollar flow rather than the credits one. */}
        {requestConsultationOpen && userId && profile && (
          <ConsultationRequestRouter
            open={requestConsultationOpen}
            onClose={closeRequestConsultation}
            expert={{
              id: userId,
              name: profile.name ?? '',
              slug: profile.url_endpoint ?? undefined,
              title: profile.title ?? undefined,
              pictureUrl: profile.picture_url ?? undefined,
              timezone: profile.timezone ?? undefined,
              clientRate: profile.client_rate,
              creditRate: profile.credit_rate,
            }}
            profile={profile}
            expertId={userId}
          />
        )}
      </div>
    );
  }
);
ChatHeader.displayName = 'ChatHeader';

export default ChatHeader;
